import * as React from 'react';
import {StyleSheet, View, ViewStyle} from 'react-native';

import * as Immutable from 'immutable';

import CharacterWithMarkView from './parts/CharacterWithMarkView';
import CharacterShowOrUpdateOrHideCommandView from './command_views/CharacterShowOrUpdateOrHideCommandView';

import CharacterHideCommand from '../../../../../domain/entities/commands/CharacterHideCommand';
import CharacterShowCommand from '../../../../../domain/entities/commands/CharacterShowCommand';
import CharacterUpdateCommand from '../../../../../domain/entities/commands/CharacterUpdateCommand';

import Position from '../../../../../domain/value_objects/Position';
import AdjustablePosition from '../../../../../domain/value_objects/AdjustablePosition';

interface Size {
  height: number;
  width: number;
}

export interface Props {
  position: Position;
  left: number;
  right: number;
  characterCommand:
    | CharacterShowCommand
    | CharacterUpdateCommand
    | CharacterHideCommand
    | null;
  characterSize: Size;
  markSize: Size;
  currentSpeechBalloonPositions: Position[] | null;
  active?: boolean;
  scaleUp?: boolean;
  zoom?: boolean;
  hidden?: boolean;
  adjustablePosition: AdjustablePosition;
  onAfterRenderCharacter: (
    command:
      | CharacterShowCommand
      | CharacterUpdateCommand
      | CharacterHideCommand,
  ) => void;
}

export interface State {
  prevLeft: number;
  prevRight: number;
}

export default class StagePositionBase extends React.Component<Props, State> {
  private mounted = false;

  private callback: (() => void) | null = null;

  constructor(props: Props) {
    super(props);
    this.state = {
      prevLeft: props.left,
      prevRight: props.right,
    };
  }

  public shouldComponentUpdate(
    nextProps: Readonly<Props>,
    nextState: Readonly<State>,
  ): boolean {
    return !(
      this.props.position === nextProps.position &&
      this.props.left === nextProps.left &&
      this.props.right === nextProps.right &&
      this.props.characterCommand === nextProps.characterCommand &&
      this.props.zoom === nextProps.zoom &&
      this.props.hidden === nextProps.hidden &&
      Immutable.is(
        this.props.currentSpeechBalloonPositions,
        nextProps.currentSpeechBalloonPositions,
      ) &&
      this.props.active === nextProps.active &&
      this.state.prevLeft === nextState.prevLeft &&
      this.state.prevRight === nextState.prevRight
    );
  }

  public componentDidMount() {
    this.mounted = true;
  }

  public componentWillUnmount() {
    this.mounted = false;
  }

  public componentDidUpdate(
    prevProps: Readonly<Props>,
    prevState: Readonly<State>,
  ) {
    if (!this.props.characterCommand) {
      return;
    }
    if (prevProps.characterCommand !== this.props.characterCommand) {
      if (this.props.characterCommand instanceof CharacterHideCommand) {
        this.resetAnimationValues();
        return;
      } else if (this.props.characterCommand instanceof CharacterShowCommand) {
        this.finishCallback();
        return;
      }
    }
    if (
      prevProps.left !== this.props.left &&
      prevProps.right !== this.props.right
    ) {
      if (
        this.props.position === Position.Center &&
        prevProps.left === prevProps.right &&
        this.props.left === this.props.right
      ) {
        this.finishCallback();
      } else {
        this.move();
      }
    } else if (prevProps.left !== this.props.left) {
      this.moveLeft();
    } else if (prevProps.right !== this.props.right) {
      this.moveRight();
    } else if (this.callback) {
      if (this.props.active) {
        const callback = this.callback;
        setTimeout(callback, 50);
        this.callback = null;
      } else {
        this.move();
      }
    }
  }

  public setCallback = (callback: () => void) => {
    this.callback = callback;
  };

  protected renderContent(): React.ReactNode {
    const {
      position,
      characterCommand,
      characterSize,
      markSize,
      currentSpeechBalloonPositions,
      active,
      scaleUp,
      hidden,
      onAfterRenderCharacter,
    } = this.props;
    return (
      <CharacterWithMarkView
        position={position}
        key={characterCommand?.character.id}
        characterCommand={characterCommand}
        characterSize={characterSize}
        markSize={markSize}
        currentSpeechBalloonPositions={currentSpeechBalloonPositions}
        active={active}
        scaleUp={scaleUp}
        hidden={hidden}
        setCallback={this.setCallback}
        onAfterRenderCharacter={onAfterRenderCharacter}
      />
    );
  }

  protected renderZoomedContent(): React.ReactNode {
    const {characterCommand, scaleUp, zoom, characterSize} = this.props;
    if (scaleUp) {
      return null;
    }
    if (!zoom) {
      return null;
    }
    if (!characterCommand) {
      return null;
    }
    return (
      <View
        style={
          {
            transform: 'scale(1.75)',
            alignItems: 'center',
            position: 'absolute',
            top: 50 * (characterSize.height / 300),
            ...this.getZoomPositionStyle(),
          } as any
        }>
        <CharacterShowOrUpdateOrHideCommandView
          command={characterCommand}
          {...this.props}
        />
      </View>
    );
  }

  protected move = () => {};

  protected moveLeft = () => {};

  protected moveRight = () => {};

  protected resetAnimationValues = () => {};

  protected finishCallback = () => {
    if (this.mounted) {
      if (
        this.state.prevLeft === this.props.left &&
        this.state.prevRight === this.props.right
      ) {
        return this.finishCallbackAfterStateChange();
      }
      this.setState(
        {prevLeft: this.props.left, prevRight: this.props.right},
        this.finishCallbackAfterStateChange,
      );
    }
  };

  private finishCallbackAfterStateChange = () => {
    if (this.callback) {
      const callback = this.callback;
      setTimeout(callback, 200);
      this.callback = null;
    }
    this.resetAnimationValues();
  };

  public getZoomPositionStyle = () => {
    const {adjustablePosition, characterSize} = this.props;
    switch (adjustablePosition) {
      case AdjustablePosition.Left:
        return {
          left: -80 * (characterSize.height / 300),
          right: 80 * (characterSize.height / 300),
        };
      case AdjustablePosition.CenterLeft:
        return {
          left: -60 * (characterSize.height / 300),
          right: 60 * (characterSize.height / 300),
        };
      case AdjustablePosition.Center:
        return {
          left: 0,
          right: 0,
        };
      case AdjustablePosition.CenterRight:
        return {
          left: 60 * (characterSize.height / 300),
          right: -60 * (characterSize.height / 300),
        };
      case AdjustablePosition.Right:
        return {
          left: 80 * (characterSize.height / 300),
          right: -80 * (characterSize.height / 300),
        };
      default: {
        return {};
      }
    }
  };
}

const styles = StyleSheet.create({
  container: {
    bottom: 0,
    overflow: 'visible',
    position: 'absolute',
    top: 0,
  } as ViewStyle,
});
